import { Observable, of } from 'rxjs';
import { map, tap, switchMap, take } from 'rxjs/operators';

import { IAddressWalklist, IGeoServer } from '../interfaces/models';
import { PageConfig, ResultGeoServer, Endpoint } from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';

import { AddressWalklist } from '../models/addressWalklist';
import { Result } from '../interfaces/helpers';
import { HttpParams } from '@angular/common/http';
import { environment } from 'env/config';
import { formatAddressName, formatStreetName } from '../lib/utils';

export class AddressWalklistPage extends Page<AddressWalklist> {
  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super(
      (endpoint as string) + 'telus/wfs',
      PageTitles.addressWalklist,
      config
    );

    this.initializeColumns({
      keys: new AddressWalklist(),
      hiddenTableFields: ['checked', 'searchStreet'],
    });
  }

  get actions(): string[] {
    return [];
  }

  fetch(queryString?): Observable<Result<AddressWalklist>> {
    const newQueryString = {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:address_extension_gs_view',
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: `WITHIN(the_geom, ${queryString.coordinates})`,
    };

    return this.getItems(newQueryString, environment.api.headers.d2cGeo);
  }

  postFetch(queryString?): Observable<Result<AddressWalklist>> {
    const newQueryString = {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:address_extension_gs_view',
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      cql_filter: `WITHIN(the_geom, ${queryString.coordinates})`,
    };

    const params = new HttpParams({
      fromObject: newQueryString,
    });

    const header = Object.assign(
      {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/x-www-form-urlencoded',
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Accept: '*/*',
      },
      environment.api.headers.d2cGeo
    );
    return this.getItemsPost(params, header);
  }

  fetchByIds(queryString?): Observable<Result<AddressWalklist>> {
    const newQueryString = {
      service: 'WFS',
      request: 'GetFeature',
      version: '2.0.0',
      typeName: 'telus:address_extension_gs_view',
      outputformat: 'application/json',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      featureID: `${queryString.ids}`,
    };

    if (queryString.sortBy) {
      newQueryString['sortBy'] = queryString.sortBy;
    }

    return this.getItems(newQueryString, environment.api.headers.d2cGeo);
  }

  fetchById(id: string): Observable<IAddressWalklist> {
    return this.getItem(id).pipe(
      map((data: IGeoServer) => this.createFromDTO(data))
    );
  }

  update(item: IAddressWalklist): Observable<AddressWalklist> {
    let updatedAddressWalklists: IAddressWalklist[];
    return this.items.pipe(
      take(1),
      switchMap((addressWalklist) => {
        if (!addressWalklist || addressWalklist.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(addressWalklist);
        }
      }),
      switchMap((addressWalklists) => {
        const addressWalklistIndex = addressWalklists.findIndex(
          (i) => i.id === item.id
        );
        updatedAddressWalklists = [...addressWalklists];
        const oldAddressWalklist =
          updatedAddressWalklists[addressWalklistIndex];
        updatedAddressWalklists[addressWalklistIndex] = new AddressWalklist({
          ...item,
          id: oldAddressWalklist.id,
        });
        return this.updateItems(updatedAddressWalklists[addressWalklistIndex]);
      }),
      tap(() => {
        this.pageItems.next(updatedAddressWalklists);
      })
    );
  }

  createFromDTO(item): AddressWalklist {
    const {
      city,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      derived_drop_permission_status,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dir_prefix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dir_suffix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      do_not_knock,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      dp_walklist_id,
      floor,
      fsa,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      postal_code,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      premise_type,
      province,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      sales_walklist_id,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      sayb_d2c_decision,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_name,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_number,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_number_prefix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_number_suffix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_type_prefix,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      street_type_suffix,
      unit,
    } = item.properties;

    const id = item.id.split('.')[1];
    const address = {
      addressId: id,
      unit,
      floor,
      streetNumberPrefix: street_number_prefix,
      streetNumber: street_number,
      streetNumberSuffix: street_number_suffix,
      dirPrefix: dir_prefix,
      streetTypePrefix: street_type_prefix,
      streetName: street_name,
      streetTypeSuffix: street_type_suffix,
      dirSuffix: dir_suffix,
      city,
      province,
      postalCode: postal_code,
      locationComments: null,
      buildingType: '',
      projectSubType: '',
    };

    return new AddressWalklist({
      id,
      postalCode: postal_code,
      unitNumber: unit,
      streetNumber: street_number,
      suffix: street_number_suffix,
      dirPrefix: dir_prefix,
      streetNameType: formatStreetName(address),
      dirSuffix: dir_suffix,
      premiseType: premise_type,
      sayb: sayb_d2c_decision,
      permissionStatus: '',
      walklistAssigned: false,
      checked: false,
      searchStreet: '',
      walklistAssignedTo: '',
      walklistId: dp_walklist_id ? dp_walklist_id : sales_walklist_id,
      serviceAddress: formatAddressName(address),
      walklistCreatedBy: '',
      walklistCreationAt: new Date(),
      walklistModifiedAt: null,
      walklistModifiedBy: '',
      walklistActive: false,
      derivedDpStatus: derived_drop_permission_status,
      doNotKnockInd: do_not_knock,
      totalFeatures: 0,
      fsa,
    });
  }
}
